import { AxiosRequestConfig } from "axios";
import { useEffect, useRef, useState } from "react";
import assetClient from "src/services/assetClient";
import { useChunkedVideoUpload } from "./useChunkedVideoUpload";

// Re-export so feature code can pick up the state type without having
// to import directly from `useChunkedVideoUpload` (which is restricted).
export type { ChunkedUploadState } from "./useChunkedVideoUpload";

/**
 * Unified upload hook used by every admin file picker.
 *
 * Behaviour by uploadType:
 *  - "video"  → routed through `useChunkedVideoUpload`. Slices the file
 *               into 10 MB chunks, posts in parallel with per-chunk
 *               retry, supports up to 50 GB, and tracks real-time
 *               progress / speed / ETA. The legacy `progress` (0..1) and
 *               `uploading` outputs continue to work, so existing callers
 *               that render a progress bar pick up accurate values for
 *               free.
 *  - everything else (images, documents) → single-shot multipart POST
 *               with axios `onUploadProgress` (unchanged).
 *
 * The decision is made at `upload(...)` call time so the same hook
 * instance can handle multiple uploads in a row of different types
 * (e.g. SocialFeedAdverts mixed image/video flow).
 *
 * Both code paths resolve `upload(...)` to `string | null` — the asset
 * reference on success or null on failure — so callers get a uniform
 * shape regardless of which transport was used. Most callers consume
 * the hook state (`done`, `videoUploadState`, etc.) instead of awaiting,
 * but any code that does await receives a consistent value.
 *
 * All uploads land in private storage; reads go through the authenticated
 * `/api/v1.0/asset/*` endpoints which return short-lived SAS URLs.
 */
export const useAzureFileUpload = (
	setUUID?: (uuid: string | null | string[]) => void,
	setIndex?: (uuid: string | null, index?: number) => void
) => {
	const [progress, setProgress] = useState<number>(0);

	const [done, setDone] = useState<boolean>(false);

	const [error, setError] = useState<boolean>(false);

	const [uploading, setUploading] = useState<boolean>(false);

	// Pending index for the chunked path — captured at upload start so
	// the success/failure callbacks can call setIndex with the right
	// index even when the chunked hook doesn't know about it.
	const pendingChunkedIndexRef = useRef<number | undefined>(undefined);

	const chunked = useChunkedVideoUpload();
	const chunkedStatus = chunked.state.status;
	const isChunkedPath =
		chunkedStatus === "initiating" ||
		chunkedStatus === "uploading" ||
		chunkedStatus === "completing";

	// Hold the latest setUUID / setIndex callbacks in refs so the mirror
	// effect doesn't need them in its dep array. Consumers pass inline
	// arrow functions (most do) that change identity on every render —
	// putting them in deps would re-fire the "done" branch on every
	// re-render, duplicating UUIDs in list-style consumers like
	// MultiMediaUploadStep.
	const setUUIDRef = useRef(setUUID);
	const setIndexRef = useRef(setIndex);
	useEffect(() => {
		setUUIDRef.current = setUUID;
		setIndexRef.current = setIndex;
	}, [setUUID, setIndex]);

	// Track which terminal status we last emitted to consumers so each
	// upload only notifies once even if the effect re-runs (it won't, with
	// the dep list below, but this is belt-and-braces).
	const lastEmittedStatusRef = useRef<string>("");

	// Mirror the chunked hook's lifecycle into the legacy state shape so
	// existing consumers don't need to know which path was used.
	useEffect(() => {
		if (chunkedStatus === "idle") {
			lastEmittedStatusRef.current = "";
			return;
		}
		if (chunkedStatus === "initiating") {
			setUploading(true);
			setError(false);
			setDone(false);
			setProgress(0);
			lastEmittedStatusRef.current = "in-flight";
			return;
		}
		if (chunkedStatus === "uploading" || chunkedStatus === "completing") {
			setUploading(true);
			setProgress(chunked.state.progress);
			lastEmittedStatusRef.current = "in-flight";
			return;
		}
		if (chunkedStatus === "done" && chunked.state.assetId) {
			if (lastEmittedStatusRef.current === "done") return;
			lastEmittedStatusRef.current = "done";
			setProgress(1);
			setUploading(false);
			setError(false);
			setDone(true);
			setUUIDRef.current?.(chunked.state.assetId);
			if (setIndexRef.current !== undefined) {
				setIndexRef.current(
					chunked.state.assetId,
					pendingChunkedIndexRef.current
				);
			}
			return;
		}
		if (chunkedStatus === "error") {
			if (lastEmittedStatusRef.current === "error") return;
			lastEmittedStatusRef.current = "error";
			setUploading(false);
			setError(true);
			setDone(false);
			return;
		}
		if (chunkedStatus === "aborted") {
			if (lastEmittedStatusRef.current === "aborted") return;
			lastEmittedStatusRef.current = "aborted";
			setUploading(false);
			setError(false);
			setDone(false);
			setProgress(0);
		}
	}, [chunkedStatus, chunked.state.progress, chunked.state.assetId]);

	const onUploadFailed = () => {
		setUploading(false);
		setError(true);
	};

	const onUploadSuccess = (result: any, index?: number) => {
		const data = result?.data;
		if (!data || (Array.isArray(data) && data.length === 0)) {
			// Empty response = upload didn't actually persist.
			onUploadFailed();
			return null;
		}

		setUUIDRef.current?.(data);
		if (setIndexRef.current) {
			setIndexRef.current(
				typeof data === "string" ? data : Array.isArray(data) ? data[0] : null,
				index
			);
		}

		setDone(true);
		setError(false);
		setUploading(false);

		// Return a single asset reference so the contract matches the
		// chunked path (Promise<string | null>). Some legacy endpoints
		// historically returned arrays; flatten to the first element.
		if (typeof data === "string") return data;
		if (Array.isArray(data) && typeof data[0] === "string") return data[0];
		return null;
	};

	const upload = async (
		file: File,
		uploadType: string,
		index?: number,
		imageProfile?: ImageProfile
	): Promise<string | null> => {
		// Videos go through the chunked pipeline regardless of size — that
		// gives 50 GB ceiling, per-chunk retry, real-time progress, and
		// resumable uploads. Caller still gets the same { progress, done,
		// error, uploading } back via the effect above, plus a uniform
		// `Promise<string | null>` return.
		if (uploadType === "video") {
			pendingChunkedIndexRef.current = index;
			lastEmittedStatusRef.current = "";
			return chunked.upload(file);
		}

		const formData = new FormData();
		formData.append("File", file);

		setUploading(true);
		setError(false);
		setDone(false);
		setProgress(0);

		const axiosConfig = {
			headers: {
				"Content-Type": "multipart/form-data",
				Accept: "application/json"
			},
			onUploadProgress: (progressEvent: any) => {
				setProgress(progressEvent.loaded / progressEvent.total);
			},
			params: {
				profileName: imageProfile
			}
		} as AxiosRequestConfig;

		try {
			const result = await assetClient.post(
				`/api/v1.0/Upload/${uploadType}`,
				formData,
				axiosConfig
			);
			return onUploadSuccess(result, index);
		} catch {
			onUploadFailed();
			return null;
		}
	};

	const clear = () => {
		setUUIDRef.current?.(null);
		setProgress(0);
		setError(false);
		setDone(false);
		lastEmittedStatusRef.current = "";
		if (isChunkedPath) {
			chunked.abort();
		} else {
			chunked.reset();
		}
	};

	return {
		progress,
		done,
		error,
		uploading,
		upload,
		clear,
		// Optional rich state for callers that want to show speed / ETA /
		// bytes during a video upload. Null for non-video uploads.
		videoUploadState:
			isChunkedPath ||
			chunkedStatus === "done" ||
			chunkedStatus === "error" ||
			chunkedStatus === "aborted"
				? chunked.state
				: null,
		abortVideoUpload: chunked.abort
	};
};
